import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AppContext";
import { listMyOrders } from "../lib/dataStore";
import { Package, ExternalLink } from "lucide-react";
import { orderGrandTotal, orderSubtotal, orderDiscount, STATUS_CONFIG } from "../lib/orderHelpers";

const statusColor = (s) => STATUS_CONFIG[s]?.badge || "bg-muted text-foreground border-border";

const isUrl = (s) => typeof s === "string" && /^https?:\/\//i.test(s.trim());

export default function MyOrders() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => { if (user) listMyOrders(user.uid).then(setOrders); }, [user]);

  if (loading) return <div className="max-w-6xl mx-auto p-16 text-center text-muted-foreground">Loading…</div>;

  if (!user) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-4xl mb-4">Sign in to see your orders</h1>
      <Link data-testid="orders-signin-link" to="/login" className="px-6 py-3 rounded-full bg-terracotta text-primary-foreground inline-block">Sign in</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="overline text-terracotta mb-2">My orders</div>
      <h1 className="font-serif text-4xl mb-10">Order history</h1>
      {orders.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center text-muted-foreground">
          <Package className="w-8 h-8 mx-auto mb-3 opacity-50" />
          You haven't placed any orders yet.
          <div className="mt-4"><Link className="text-terracotta underline underline-offset-4" to="/shop">Start shopping →</Link></div>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-border p-6 bg-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-xs text-muted-foreground font-mono">Order #{String(o.id || "").toUpperCase()}</div>
                  <div className="text-sm">{new Date(o.createdAt?.seconds ? o.createdAt.seconds * 1000 : o.createdAt).toLocaleString()}</div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full ${statusColor(o.status)}`}>{o.status}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="overline text-muted-foreground mb-1">Items</div>
                  <ul className="space-y-1">
                    {o.items.map((i, idx) => (
                      <li key={idx}>
                        • {i.name}{i.variantTag && i.variantTag !== "Default" ? ` [${i.variantTag}]` : ""} × {i.quantity} — ₹{i.price * i.quantity}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="overline text-muted-foreground mb-1">Shipping & Payment</div>
                  <div>{o.address}, {o.city} {o.pincode}</div>
                  <div className="mt-2 text-xs">
                    <span className="text-muted-foreground">Payment: </span>
                    <strong className="text-foreground">{o.paymentMethod || "Prepaid"}</strong>
                    {o.paymentType === "COD" || o.paymentMethod?.includes("COD") || Number(o.codCharge || 0) > 0 ? (
                      <div className="mt-1 space-y-0.5 text-[11px]">
                        <span className="block text-emerald-700 dark:text-emerald-400 font-medium">
                          Prepaid Advance: ₹{o.advancePaid != null ? o.advancePaid : 0}
                        </span>
                        <span className="block text-amber-800 dark:text-amber-300 font-bold">
                          Balance Due on Delivery: ₹{o.codBalanceDue != null ? o.codBalanceDue : Math.max(0, orderGrandTotal(o) - Number(o.advancePaid || 0))} (Cash)
                        </span>
                        {Number(o.codCharge || 0) > 0 && (
                          <span className="block text-muted-foreground text-[10px]">
                            (Includes ₹{o.codCharge} COD logistics fee)
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      {orderDiscount(o) > 0 ? (
                        <span className="text-sm">
                          Total: <span className="font-price text-muted-foreground line-through">₹{orderSubtotal(o)}</span> <strong className="font-price text-terracotta text-base font-bold">₹{orderGrandTotal(o)}</strong>
                          <span className="font-price text-xs text-muted-foreground ml-1">(₹{orderDiscount(o)} off)</span>
                        </span>
                      ) : (
                        <span className="text-sm">Total: <strong className="text-base font-price font-bold">₹{orderGrandTotal(o)}</strong></span>
                      )}

                      {(o.consignmentNumber || o.trackingNumber) && (
                        <span className="text-xs bg-muted/80 px-2.5 py-1 rounded font-mono border border-border">
                          Consignment: <strong>{o.consignmentNumber || o.trackingNumber}</strong>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Link
                        data-testid={`track-link-${o.id}`}
                        to={`/track/${o.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-full bg-terracotta text-primary-foreground hover:opacity-90 transition shadow-2xs"
                      >
                        <Package className="w-3.5 h-3.5" /> Track Shipment
                      </Link>

                      {o.invoiceUrl && (
                        <a href={o.invoiceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-full border border-border hover:bg-muted transition">
                          Invoice <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
